import { useMemo, useRef, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  COPACK_FILTRES,
  COPACK_FILTRE_LABELS,
  RECONCILIATION_ANCHOR_DATE,
  RECONCILIATION_BANNER,
  type CopackFiltre,
  type ReconciliationQueryInput,
  type ReconciliationRow,
} from '@kn/shared';
import { api } from '../api/client';
import { messageErreur } from '../api/error-message';
import { BadgeCopack } from '../components/BadgeCopack';
import { classeMontant } from '../components/montant';

const RECONCILIATION_QUERY_KEY = ['reconciliation'] as const;

/** Hauteur d'une ligne du tableau virtualise, en pixels. */
const HAUTEUR_LIGNE = 34;

type Maille = 'ARTICLE' | 'FAMILLE';

function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function formaterTaux(taux: number): string {
  return `${(taux * 100).toLocaleString('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
}

function formaterDate(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return jour && mois && annee ? `${jour}/${mois}/${annee}` : iso;
}

export function RapprochementPage(): ReactElement {
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [maille, setMaille] = useState<Maille>('ARTICLE');
  const [inclureHorsControle, setInclureHorsControle] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [copack, setCopack] = useState<CopackFiltre | ''>('');

  const filtres: ReconciliationQueryInput = useMemo(
    () => ({
      ...(du ? { du } : {}),
      ...(au ? { au } : {}),
      maille,
      inclureHorsControle,
      ...(recherche ? { search: recherche } : {}),
      ...(copack ? { copack } : {}),
    }),
    [du, au, maille, inclureHorsControle, recherche, copack],
  );

  const rapprochement = useQuery({
    queryKey: [...RECONCILIATION_QUERY_KEY, filtres],
    queryFn: () => api.reconcile(filtres),
  });

  // Le KPI porte sur une autre population que le tableau : requête séparée, sur
  // les seules bornes de période — la maille et les filtres ne le concernent pas.
  const bornesKpi = useMemo(
    () => ({ ...(du ? { du } : {}), ...(au ? { au } : {}), maille: 'ARTICLE' as const }),
    [du, au],
  );
  const kpi = useQuery({
    queryKey: [...RECONCILIATION_QUERY_KEY, 'kpi', bornesKpi],
    queryFn: () => api.kpiEntrees(bornesKpi),
  });

  const rows: ReconciliationRow[] = rapprochement.data?.rows ?? [];
  const conteneur = useRef<HTMLDivElement>(null);

  // Des dizaines de milliers de lignes : seule la portion visible est rendue.
  const virtualiseur = useVirtualizer({
    count: rows.length,
    getScrollElement: () => conteneur.current,
    estimateSize: () => HAUTEUR_LIGNE,
    overscan: 20,
  });

  const periode = rapprochement.data?.periodeDisponible;
  const totals = rapprochement.data?.totals;

  // Les colonnes d'ouverture ne portent une information que si la periode
  // demarre apres l'ancrage : sinon elles repetent le stock initial.
  const debutPeriode = rapprochement.data?.du ?? RECONCILIATION_ANCHOR_DATE;
  const afficherOuverture = debutPeriode !== RECONCILIATION_ANCHOR_DATE;
  const classeGrille = afficherOuverture
    ? 'rappro-grille rappro-grille--ouverture'
    : 'rappro-grille';

  return (
    <>
      <h1>Rapprochement brut</h1>

      <p className="bandeau-avertissement" role="note">
        <strong>{RECONCILIATION_BANNER}</strong>
        <span>
          Les deux comptabilites partent de l&apos;inventaire physique du{' '}
          {formaterDate(RECONCILIATION_ANCHOR_DATE)}. Tous les mouvements sont comptes, sans
          neutralisation et sans tolerance : un écart affiche ici est une différence constatee, pas
          encore une anomalie.
        </span>
      </p>

      <div className="carte">
        <div className="barre-actions">
          <label className="champ" htmlFor="rappro-du">
            <span>Du</span>
            <input
              id="rappro-du"
              type="date"
              value={du || debutPeriode}
              onChange={(event) => setDu(event.target.value)}
            />
          </label>
          <label className="champ" htmlFor="rappro-au">
            <span>Au</span>
            <input
              id="rappro-au"
              type="date"
              value={au || (periode?.au ?? '')}
              onChange={(event) => setAu(event.target.value)}
            />
          </label>
          <label className="champ" htmlFor="rappro-maille">
            <span>Maille</span>
            <select
              id="rappro-maille"
              value={maille}
              onChange={(event) => setMaille(event.target.value as Maille)}
            >
              <option value="ARTICLE">Par article</option>
              <option value="FAMILLE">Par famille d&apos;équivalence</option>
            </select>
          </label>
          <label className="champ" htmlFor="rappro-hors-controle">
            <span>Articles hors périmètre</span>
            <select
              id="rappro-hors-controle"
              value={inclureHorsControle ? 'oui' : 'non'}
              onChange={(event) => setInclureHorsControle(event.target.value === 'oui')}
            >
              <option value="non">Masqués</option>
              <option value="oui">Affichés</option>
            </select>
          </label>
          <label className="champ" htmlFor="rappro-copack">
            <span>Copacking</span>
            <select
              id="rappro-copack"
              value={copack}
              onChange={(event) => setCopack(event.target.value as CopackFiltre | '')}
            >
              <option value="">Tous</option>
              {COPACK_FILTRES.map((valeur) => (
                <option key={valeur} value={valeur}>
                  {COPACK_FILTRE_LABELS[valeur]}
                </option>
              ))}
            </select>
          </label>
          <label className="champ" htmlFor="rappro-recherche">
            <span>Rechercher</span>
            <input
              id="rappro-recherche"
              type="search"
              placeholder="Code ou libellé"
              value={recherche}
              onChange={(event) => setRecherche(event.target.value)}
            />
          </label>
          <a
            className="bouton"
            href={api.reconciliationExportUrl(filtres)}
            aria-disabled={rows.length === 0}
          >
            Exporter en Excel
          </a>
        </div>

        {kpi.data && (
          <div className="kpi">
            <div className="kpi__mesure kpi__mesure--principale">
              <span>Entrées alignées hors ODE</span>
              <strong>{formaterTaux(kpi.data.horsOde.taux)}</strong>
              <small>
                {kpi.data.horsOde.alignes.toLocaleString('fr-FR')} articles sur{' '}
                {kpi.data.horsOde.articles.toLocaleString('fr-FR')}, à moins d&apos;une UVC près
              </small>
            </div>
            <div className="kpi__mesure">
              <span>Entrées alignées avec ODE</span>
              <strong>{formaterTaux(kpi.data.avecOde.taux)}</strong>
              <small>
                {kpi.data.avecOde.alignes.toLocaleString('fr-FR')} articles sur{' '}
                {kpi.data.avecOde.articles.toLocaleString('fr-FR')}
              </small>
            </div>
            <div className="kpi__mesure">
              <span>Poids des ODE</span>
              <strong>{formaterTaux(kpi.data.poidsOdePoints)}</strong>
              <small>
                {formaterNombre(kpi.data.poidsOdeUvc)} UVC déclarées sans rattachement à une
                réception
              </small>
            </div>
          </div>
        )}

        {rapprochement.isPending && <p className="chargement">Calcul du rapprochement...</p>}
        {rapprochement.isError && (
          <p className="alerte alerte--erreur">{messageErreur(rapprochement.error)}</p>
        )}

        {rapprochement.data && rows.length === 0 && (
          <p className="alerte alerte--info" role="status">
            Aucun article sur la période. Importez l&apos;inventaire d&apos;ancrage et les sources
            de mouvements depuis l&apos;écran Imports.
          </p>
        )}

        {rapprochement.data && rows.length > 0 && totals && (
          <>
            <div className="totaux">
              <div>
                <span>{maille === 'FAMILLE' ? 'Familles' : 'Articles'}</span>
                <strong>{totals.lignes.toLocaleString('fr-FR')}</strong>
              </div>
              <div>
                <span>{`Stock au ${formaterDate(RECONCILIATION_ANCHOR_DATE)}`}</span>
                <strong>{formaterNombre(totals.stockInitial)}</strong>
              </div>
              <div>
                <span>Stock final WMS</span>
                <strong>{formaterNombre(totals.wmsStockFinal)}</strong>
              </div>
              <div>
                <span>Stock final douane</span>
                <strong>{formaterNombre(totals.douaneStockFinal)}</strong>
              </div>
              <div>
                <span>Écart de stock</span>
                <strong className={classeMontant(totals.ecartStock)}>
                  {formaterNombre(totals.ecartStock)}
                </strong>
              </div>
              <div>
                <span>Somme des |écarts|</span>
                <strong>{formaterNombre(totals.sommeEcartsAbsolus)}</strong>
              </div>
            </div>

            {/* Bandeau de groupes : c'est la comparaison des deux comptabilites
                qui structure le tableau. Les en-tetes de colonnes sont courts
                (« Entrees », « Net »…) et ne prennent leur sens que sous lui —
                il n'est donc pas decoratif et reste lu par les lecteurs d'ecran. */}
            <div className={`${classeGrille} rappro-groupes`}>
              <span />
              <span />
              <span />
              <span className="rappro-groupe rappro-groupe--wms">Comptabilité physique (WMS)</span>
              <span className="rappro-groupe rappro-groupe--douane">
                Mouvements déclarés (GEJNM)
              </span>
              <span />
            </div>

            <div className={`${classeGrille} grille-entetes`}>
              <span>{maille === 'FAMILLE' ? 'Famille' : 'Code article'}</span>
              <span>Libellé · Copack</span>
              <span className="droite">{`Stock ${formaterDate(RECONCILIATION_ANCHOR_DATE)}`}</span>
              {afficherOuverture && <span className="droite">Ouverture</span>}
              <span className="droite">Entrées</span>
              <span className="droite">Sorties</span>
              <span className="droite">Net</span>
              <span className="droite">Stock final</span>
              {afficherOuverture && <span className="droite">Ouverture</span>}
              <span className="droite">Entrées</span>
              <span className="droite">Sorties</span>
              <span className="droite">Net</span>
              <span className="droite">Stock final</span>
              <span className="droite">Écart de stock</span>
            </div>

            <div className="grille-virtuelle" ref={conteneur}>
              <div style={{ height: `${virtualiseur.getTotalSize()}px`, position: 'relative' }}>
                {virtualiseur.getVirtualItems().map((element) => {
                  const row = rows[element.index]!;
                  return (
                    <div
                      key={row.cle}
                      className={`${classeGrille} grille-ligne`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${element.size}px`,
                        transform: `translateY(${element.start}px)`,
                      }}
                    >
                      <span title={row.codpros.join(' / ')}>
                        {maille === 'FAMILLE' ? row.codpros.join(' / ') : row.cle}
                      </span>
                      <span className="rappro-libelle" title={row.libelle}>
                        {row.copack && <BadgeCopack role={row.copack} />}
                        <span className="rappro-libelle__texte">{row.libelle}</span>
                      </span>
                      <span className="droite">{formaterNombre(row.stockInitial)}</span>
                      {afficherOuverture && (
                        <span className="droite">{formaterNombre(row.wmsOuverture)}</span>
                      )}
                      <span className="droite">{formaterNombre(row.wmsEntrees)}</span>
                      <span className="droite">{formaterNombre(row.wmsSorties)}</span>
                      <span className="droite">{formaterNombre(row.wmsNet)}</span>
                      <span className="droite">{formaterNombre(row.wmsStockFinal)}</span>
                      {afficherOuverture && (
                        <span className="droite">{formaterNombre(row.douaneOuverture)}</span>
                      )}
                      <span className="droite">{formaterNombre(row.douaneEntrees)}</span>
                      <span className="droite">{formaterNombre(row.douaneSorties)}</span>
                      <span className="droite">{formaterNombre(row.douaneNet)}</span>
                      <span className="droite">{formaterNombre(row.douaneStockFinal)}</span>
                      <span className={`droite ${classeMontant(row.ecartStock)}`}>
                        {formaterNombre(row.ecartStock)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="sous-titre">
              Tri par écart de stock absolu decroissant. Données disponibles jusqu&apos;au{' '}
              {periode?.au ? formaterDate(periode.au) : '—'}.
            </p>
          </>
        )}
      </div>
    </>
  );
}
